import React, { useMemo, useState } from 'react';
import styles from './gallery-images.module.scss';
import { useSwipedChange } from '@/store/useSwipedChange';
import { Photo } from '@/types/profile';
import clsx from 'clsx';
import { Pagination } from './componenets/pagination';
import { Navigation } from './componenets/navigation';
import GalleryImage from '../gallery-image/gallery-image';
import WebApp from '@twa-dev/sdk';
import { useVibration } from '@/store/useVibration';
import { Button, Typography } from '@/ui-kit';
import NotAccessPhotosIcon from '@/assets/images/not-access-photos-icon.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { routesApp } from '@/constans/routes';
import { useAuth } from '@/store/useAuth';
import { emitViewPhotoLimitEvent } from '@/utils/emit-view-photo-limit-event';
import { useNavHistory } from '@/store/useNavHistory';

interface Props {
    photos: Photo[];
    isVisibleDetails?: boolean;
    isMatched?: boolean;
    isCurrentCard?: boolean;
    imgStyle?: React.CSSProperties;
    isNeedRestrictGalleryWidget?: boolean;
    onSlideChange?: (slide: number) => void;
}

export const GalleryImages: React.FC<Props> = ({
    photos,
    isVisibleDetails,
    isMatched,
    isCurrentCard,
    imgStyle = {},
    isNeedRestrictGalleryWidget = true,
    onSlideChange,
}) => {
    const { isSwiping } = useSwipedChange((state) => state);
    const [currentIndexGallery, setCurrentIndexGallery] = useState(0);
    const currentGalleryImage = photos[currentIndexGallery];
    const dir = useSwipedChange((state) => state.dir);

    const { isVibrationEnabled } =
        useVibration(state => state);

    const userData = useAuth((state) => state.getUser());

    const emitEvent = (index: number) => {
        if (userData?.photo_parity_enabled) {
            const showRestrictGalleryWidget = calcShowRestrictGalleryWidget(index)
            if (showRestrictGalleryWidget)
                emitViewPhotoLimitEvent()
        }
    }

    const handleChangeGallery = (idx: number, dir: 'left' | 'right') => {
        if (isSwiping) return;

        if (dir === 'left' && idx > 0) {
            const index = idx - 1
            setCurrentIndexGallery(index);
            onSlideChange?.(index)
            emitEvent(index)
        }

        if (dir === 'right' && idx < photos.length - 1) {
            const index = idx + 1
            setCurrentIndexGallery(index);
            onSlideChange?.(index)
            emitEvent(index)
        }

        if (isVibrationEnabled) {
            WebApp
                .HapticFeedback
                .impactOccurred('medium');
            // pass for not android
            navigator.vibrate?.(10)
        }
    };

    const getGalleryImageClasses = (idx: number) => {
        const isMatchImage = idx === currentIndexGallery;

        return clsx(styles.image, {
            [styles.galleryImageHidden]: !isMatchImage,
            [styles.matchedImage]: isMatched,
            [styles.imageEmpty]: !currentGalleryImage?.main_url,
            ...(isCurrentCard && {
                [styles.gradientRight]: dir === 'right',
                [styles.gradientLeft]: dir === 'left',
            }),
            [styles.notAccessPhoto]: showRestrictGalleryWidget && (
                (
                    location.pathname === routesApp.browse &&
                    isCurrentCard
                ) ||
                location.pathname !== routesApp.browse
            ),
        });
    };

    const navigate = useNavigate();

    const { prevRoute } = useNavHistory(state => state);
    const location = useLocation();
    
    const calcShowRestrictGalleryWidget = (index: number) => {
        return ((userData?.photos?.length ?? 0) - 1 < index) &&
            !(userData?.is_paid ?? false) &&
            userData?.state !== 'half_done' &&
            (userData?.photo_parity_enabled ?? false) &&
            (
                (
                    location.pathname.startsWith('/profile-view/') &&
                    prevRoute !== routesApp.messages &&
                    !prevRoute?.startsWith('/messages/chat/')
                ) ||
                !location.pathname.startsWith('/profile-view/')
            ) &&
            isNeedRestrictGalleryWidget
    }

    const showRestrictGalleryWidget = useMemo(() => calcShowRestrictGalleryWidget(currentIndexGallery), [currentIndexGallery])

    return (
        <div className={clsx(styles.wrapper, { [styles.visibleDetails]: isVisibleDetails, 'gallery-images--not-access-photos': showRestrictGalleryWidget })}>
            {
                (
                    (
                        isCurrentCard ||
                        location.pathname !== routesApp.browse
                    ) &&
                    showRestrictGalleryWidget
                ) &&
                <div className={styles.notAccessPhotosBg} />
            }
            <Pagination
                className={styles.pagination}
                total={photos.length}
                activeIndex={currentIndexGallery}
                isVisible={photos.length > 1} />
            {photos.map((photo, idx) => (
                <GalleryImage
                    className={getGalleryImageClasses(idx)}
                    key={photo.id}
                    image={photo}
                    imgStyle={imgStyle} />
            ))}
            <Navigation
                currentIndex={currentIndexGallery}
                onChange={handleChangeGallery} />
            {
                (
                    (
                        isCurrentCard ||
                        location.pathname !== routesApp.browse
                    ) &&
                    showRestrictGalleryWidget
                ) &&
                <div className={styles.notAccessPhotos}>
                    <div className={styles.notAccessPhotosInfo}>
                        <img
                            src={NotAccessPhotosIcon}
                            width={56}
                            height={56}
                            className={styles.notAccessPhotosIcon} />
                        <Typography
                            typeColor="white"
                            fontWeight="semibold"
                            variant="h4"
                            className={styles.cardInfoText}>
                            Показано столько фото, сколько загружено у тебя
                        </Typography>
                        <Typography
                            typeColor="white"
                            className={styles.cardInfoText}>
                            Чтобы увидеть больше, добавь больше своих фото
                        </Typography>
                    </div>
                    <Button
                        textColor="black"
                        typeColor="white"
                        className={styles.notAccessPhotosBtn}
                        onClick={() => navigate(routesApp.profileEdit)}>
                        Добавить фото
                    </Button>
                </div>
            }
        </div>
    );
};
